import React from 'react';
import {useNavigate} from 'react-router-dom';
import {cn, useModalControls} from '@chekin/guestapp-ui-kit';
import {Analytics} from '@chekin/shared';
import {NotificationModal} from 'components/NotificationModal';
import {StatusText} from 'components/StatusText';
import {
  NavigationTitle,
  NavigationContainer,
  NavigationItem as NavigationItemWrapper,
  NavigationTexts,
  NextArrow,
  NavigationDescription,
  NavigationIconContainer,
} from './styled';

type NavigationNotificationProps = {
  title: string;
  message: string | JSX.Element;
};

type NavigationItemProps = {
  testId?: string;
  title: string;
  completed?: boolean;
  path: string | null;
  icon?: JSX.Element;
  isStatusBoxVisible?: boolean;
  description?: string;
  maxWidth?: string;
  flat?: boolean;
  notification?: NavigationNotificationProps;
};
const NavigationItem = ({
  testId,
  title,
  icon,
  completed,
  path,
  isStatusBoxVisible = true,
  description,
  maxWidth,
  flat,
  notification,
}: NavigationItemProps) => {
  const {
    isOpen: isOpenNotificationModal,
    closeModal: closeNotificationModal,
    openModal: openNotificationModal,
  } = useModalControls();
  const navigate = useNavigate();

  const goToView = React.useCallback(() => {
    Analytics.track('Navigation Item goToView', {
      path,
    });
    if (path) navigate(`../${path}`);
  }, [navigate, path]);

  const clickHandler = React.useCallback(() => {
    Analytics.track('Navigation Item Clicked', {
      notification: notification?.title,
    });
    if (notification) {
      openNotificationModal();
    } else {
      goToView();
    }
  }, [goToView, openNotificationModal, notification]);

  return (
    <NavigationContainer
      $maxWidth={maxWidth}
      $flat={flat}
      className={cn(
        'nav-item navigation-item-container',
        'kiosko:rounded-[28px] kiosko:border-[2px]',
        !flat && 'kiosko:p-9',
        flat && 'kiosko:px-5 kiosko:py-8',
      )}
      onClick={clickHandler}
      $completed={completed}
      data-testid={testId}
    >
      <NavigationItemWrapper $isClicked={!!path}>
        <NavigationIconContainer className="navigation-item-container__icon-container kiosko:mr-9">
          <div
            className={cn(
              'w-10 h-10 rounded-full flex items-center justify-center [background-color:var(--background-secondary)]',
              'kiosko:w-[80px] kiosko:h-[80px]',
            )}
          >
            {icon}
          </div>
        </NavigationIconContainer>
        <NavigationTexts className="nav-item__content kiosko:gap-[18px] kiosko:flex kiosko:flex-col">
          <NavigationTitle
            className={cn('nav-item__title', 'kiosko:text-kiosko-m kiosko:font-semibold')}
          >
            {title}
          </NavigationTitle>
          {isStatusBoxVisible && <StatusText completed={!!completed} />}
          {description && (
            <NavigationDescription
              className={cn(
                'nav-item__description',
                'kiosko:text-kiosko-s kiosko:leading-[48px] kiosko:mr-[48px]',
              )}
            >
              {description}
            </NavigationDescription>
          )}
        </NavigationTexts>
      </NavigationItemWrapper>
      <NextArrow name="grayRightArrow" className="kiosko:w-4 kiosko:h-8" />
      {notification && (
        <NotificationModal
          title={notification?.title}
          isOpenModalSheet={isOpenNotificationModal}
          onClose={goToView}
          text={notification?.message}
          withModalMobileCloseButton={false}
          closeOnDocumentClick={false}
          onConfirm={() => {
            closeNotificationModal();
            goToView();
          }}
        />
      )}
    </NavigationContainer>
  );
};

export {NavigationItem};
